import type { Offering, OfferingFaq, OfferingPdfFile } from '@goenhub/shared';
import { doc, onSnapshot } from 'firebase/firestore';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { PdfUploader } from '@/components/line/PdfUploader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { db } from '@/lib/firebase';
import {
  useCreateOffering,
  useUpdateOffering,
  type OfferingRecord,
} from '@/hooks/useLineData';

interface FormValues {
  name: string;
  descriptionText: string;
  recommendedFor: string;
  websiteUrl: string;
}

const defaults: FormValues = {
  name: '',
  descriptionText: '',
  recommendedFor: '',
  websiteUrl: '',
};

interface OfferingFormDialogProps {
  open: boolean;
  tag: 'service' | 'product';
  existing: OfferingRecord | null;
  onOpenChange: (open: boolean) => void;
}

export function OfferingFormDialog({ open, tag, existing, onOpenChange }: OfferingFormDialogProps) {
  const createMutation = useCreateOffering();
  const updateMutation = useUpdateOffering();
  const form = useForm<FormValues>({ defaultValues: defaults });
  const [faq, setFaq] = useState<OfferingFaq[]>([]);

  // Cloud Function 経由で pdfFiles が更新されるため、Firestore を直接購読する
  const [livePdfFiles, setLivePdfFiles] = useState<OfferingPdfFile[]>([]);

  useEffect(() => {
    if (existing) {
      form.reset({
        name: existing.name ?? '',
        descriptionText: existing.descriptionText ?? '',
        recommendedFor: existing.recommendedFor ?? '',
        websiteUrl: existing.websiteUrl ?? '',
      });
      setFaq(existing.faq ?? []);
      setLivePdfFiles(existing.pdfFiles ?? []);
    } else if (open) {
      form.reset(defaults);
      setFaq([]);
      setLivePdfFiles([]);
    }
  }, [existing, open, form]);

  // 編集モード時は offerings/{id} を購読し、Storage trigger による pdfFiles 更新を即時反映
  useEffect(() => {
    if (!existing?.id || !open) return;
    const unsub = onSnapshot(doc(db, 'offerings', existing.id), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as Offering;
      setLivePdfFiles(data.pdfFiles ?? []);
    });
    return () => unsub();
  }, [existing?.id, open]);

  const addFaq = () => setFaq([...faq, { question: '', answer: '' }]);
  const removeFaq = (i: number) => setFaq(faq.filter((_, idx) => idx !== i));
  const updateFaq = (i: number, key: 'question' | 'answer', val: string) => {
    setFaq(faq.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)));
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const payload = {
        name: values.name,
        tag,
        descriptionText: values.descriptionText,
        recommendedFor: values.recommendedFor,
        websiteUrl: values.websiteUrl || null,
        faq: faq.filter((f) => f.question && f.answer),
      } as Parameters<typeof createMutation.mutateAsync>[0];

      if (existing) {
        await updateMutation.mutateAsync({ id: existing.id, data: payload });
        toast.success('更新しました');
        onOpenChange(false);
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(
          '作成しました。続けて PDF をアップロードしたい場合は、一覧から編集してください。',
        );
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {existing ? '編集' : '新規作成'} — {tag === 'service' ? 'サービス' : '製品'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="o-name">名称</Label>
            <Input id="o-name" {...form.register('name', { required: true })} />
          </div>
          <div>
            <Label htmlFor="o-desc">説明テキスト</Label>
            <Textarea id="o-desc" rows={4} {...form.register('descriptionText')} />
          </div>
          <div>
            <Label htmlFor="o-recommended">おすすめの方</Label>
            <Textarea
              id="o-recommended"
              rows={3}
              placeholder="例: 30〜50代の女性経営者、敏感肌の方、ナチュラル志向の方など"
              {...form.register('recommendedFor')}
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              LINE Bot の「自分にあってる？」質問で参照されます
            </p>
          </div>
          <div>
            <Label htmlFor="o-web">Web サイト URL</Label>
            <Input id="o-web" type="url" {...form.register('websiteUrl')} />
          </div>

          <div>
            <Label className="mb-2 block">PDF ファイル</Label>
            {existing ? (
              <PdfUploader offeringId={existing.id} pdfFiles={livePdfFiles} />
            ) : (
              <p className="text-xs text-[var(--text-muted)] italic">
                先に基本情報を保存してから、編集画面で PDF をアップロードできます
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="mb-0">FAQ</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addFaq}>
                <Plus />
                追加
              </Button>
            </div>
            <div className="space-y-2">
              {faq.length === 0 && (
                <p className="text-xs text-[var(--text-muted)] italic">FAQ は未登録です</p>
              )}
              {faq.map((f, i) => (
                <div
                  key={i}
                  className="flex gap-2 items-start p-3 bg-[var(--bg-elevated)] rounded-sm"
                >
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Q. 質問"
                      value={f.question}
                      onChange={(e) => updateFaq(i, 'question', e.target.value)}
                    />
                    <Textarea
                      rows={2}
                      placeholder="A. 回答"
                      value={f.answer}
                      onChange={(e) => updateFaq(i, 'answer', e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFaq(i)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              キャンセル
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? '保存中...' : existing ? '更新' : '作成'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
